<template>
  <section class="processing-shell">
    <div class="processing-orbit"></div>
    <div class="processing-orbit orbit-two"></div>

    <div class="processing-card">
      <div class="processing-badge" :class="statusTone">
        <span class="status-dot"></span>
        <span class="status-text">{{ statusLabel }}</span>
      </div>

      <div class="processing-hero">
        <p class="eyebrow">Profile setup in progress</p>
        <h1>We are shaping your Screened profile.</h1>
        <p class="lead">
          Our AI is parsing your resume, structuring your experience, and preparing your profile to be
          shared with hiring teams. This usually takes a few minutes.
        </p>
      </div>

      <div class="processing-steps">
        <div class="step" v-for="step in steps" :key="step.title">
          <div class="step-index">{{ step.index }}</div>
          <div>
            <p class="step-title">{{ step.title }}</p>
            <p class="step-copy">{{ step.copy }}</p>
          </div>
        </div>
      </div>

      <div class="processing-footer">
        <div class="processing-meta">
          <div>
            <p class="meta-label">Last check</p>
            <p class="meta-value">{{ lastCheckedLabel }}</p>
          </div>
          <div>
            <p class="meta-label">Resume file</p>
            <p class="meta-value">{{ resumeName }}</p>
          </div>
        </div>

        <div class="processing-actions">
          <button class="primary" :disabled="checking" @click="refreshStatus">
            {{ checking ? 'Checking status...' : 'Refresh status' }}
          </button>
          <button class="secondary" @click="redirectToProfile" :disabled="!canContinue">
            Continue to profile
          </button>
        </div>
      </div>

      <div v-if="status === 'failed'" class="processing-alert">
        <p>We ran into an issue while reading your resume. Please contact support and we will help finalize your profile.</p>
      </div>

      <p class="processing-help">
        Need help? Reach out to <a href="mailto:recruit@screened.com.au">recruit@screened.com.au</a> and we will take a look.
      </p>
    </div>
  </section>
</template>

<script>
import contractorService from '@/services/marketplace/contractor.service';

const POLL_INTERVAL_MS = 12000;

export default {
  name: 'ContractorProcessing',
  data() {
    return {
      status: 'loading',
      lastChecked: null,
      resumeName: 'Pending upload',
      checking: false,
      pollId: null,
      redirectTimeout: null,
      steps: [
        {
          index: '01',
          title: 'Reading your resume',
          copy: 'We extract roles, companies, and timelines for an accurate profile summary.'
        },
        {
          index: '02',
          title: 'Structuring your profile',
          copy: 'Your experience is mapped into a professional layout companies can scan quickly.'
        },
        {
          index: '03',
          title: 'Final quality checks',
          copy: 'We verify the essentials before your profile goes live.'
        }
      ]
    };
  },
  computed: {
    statusLabel() {
      if (this.status === 'ready') return 'Profile ready';
      if (this.status === 'failed') return 'Action needed';
      if (this.status === 'loading') return 'Checking status';
      return 'Processing resume';
    },
    statusTone() {
      if (this.status === 'ready') return 'tone-ready';
      if (this.status === 'failed') return 'tone-failed';
      return 'tone-processing';
    },
    lastCheckedLabel() {
      if (!this.lastChecked) return '—';
      return this.lastChecked.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
    canContinue() {
      return this.status === 'ready';
    }
  },
  methods: {
    async refreshStatus() {
      if (this.checking) return;
      this.checking = true;
      try {
        const response = await contractorService.getUploads();
        const data = response.data || {};

        if (data.active) {
          this.status = 'ready';
          this.resumeName = data.active.file_name || 'Resume parsed';
          sessionStorage.setItem('contractorReadyStatus', 'true');
          sessionStorage.setItem('contractorReadyStatusAt', Date.now().toString());
          this.autoRedirect();
        } else if (data.failed) {
          this.status = 'failed';
          this.resumeName = data.failed.file_name || 'Resume processing issue';
          sessionStorage.setItem('contractorReadyStatus', 'false');
          sessionStorage.setItem('contractorReadyStatusAt', Date.now().toString());
        } else if (data.pending) {
          this.status = 'processing';
          this.resumeName = data.pending.file_name || 'Resume uploaded';
          sessionStorage.setItem('contractorReadyStatus', 'false');
          sessionStorage.setItem('contractorReadyStatusAt', Date.now().toString());
        } else {
          this.status = 'processing';
          this.resumeName = 'Resume uploaded';
          sessionStorage.setItem('contractorReadyStatus', 'false');
          sessionStorage.setItem('contractorReadyStatusAt', Date.now().toString());
        }

        this.lastChecked = new Date();
      } catch (error) {
        this.status = 'processing';
      } finally {
        this.checking = false;
      }
    },
    autoRedirect() {
      if (this.redirectTimeout) return;
      this.redirectTimeout = setTimeout(() => {
        this.redirectToProfile();
      }, 1200);
    },
    redirectToProfile() {
      const redirectUri = this.$route.query.redirect_uri;
      this.$router.push(redirectUri || '/contractor/profile');
    },
    startPolling() {
      this.pollId = setInterval(() => {
        if (this.status !== 'ready') {
          this.refreshStatus();
        }
      }, POLL_INTERVAL_MS);
    }
  },
  mounted() {
    this.refreshStatus();
    this.startPolling();
  },
  beforeUnmount() {
    if (this.pollId) {
      clearInterval(this.pollId);
    }
    if (this.redirectTimeout) {
      clearTimeout(this.redirectTimeout);
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Fraunces:wght@500;600;700&family=Space+Grotesk:wght@400;500;600&display=swap');

.processing-shell {
  --ink: #0f172a;
  --muted: #5b6b85;
  --accent: #1d4ed8;
  --accent-soft: rgba(29, 78, 216, 0.12);
  --surface: rgba(255, 255, 255, 0.92);
  --border: rgba(148, 163, 184, 0.2);
  --success: #0f766e;
  --warning: #b45309;

  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background:
    radial-gradient(circle at 15% 20%, rgba(59, 130, 246, 0.18), transparent 45%),
    radial-gradient(circle at 85% 0%, rgba(99, 102, 241, 0.22), transparent 40%),
    linear-gradient(130deg, #f8fafc 0%, #eef2ff 45%, #fef9f6 100%);
  font-family: 'Space Grotesk', sans-serif;
  color: var(--ink);
  position: relative;
  overflow: hidden;
}

.processing-orbit {
  position: absolute;
  width: 320px;
  height: 320px;
  border-radius: 50%;
  border: 1px solid rgba(59, 130, 246, 0.18);
  top: 10%;
  left: -120px;
  animation: orbitFloat 14s ease-in-out infinite;
}

.orbit-two {
  width: 420px;
  height: 420px;
  right: -160px;
  left: auto;
  top: 50%;
  border-color: rgba(251, 146, 60, 0.2);
  animation-delay: -4s;
}

.processing-card {
  width: min(1080px, 100%);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 32px;
  padding: 48px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.12);
  backdrop-filter: blur(12px);
  animation: rise 0.6s ease;
  position: relative;
  z-index: 1;
}

.processing-badge {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  border-radius: 999px;
  border: 1px solid transparent;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-weight: 600;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
  box-shadow: 0 0 0 6px rgba(15, 23, 42, 0.08);
}

.tone-processing {
  color: var(--accent);
  background: var(--accent-soft);
  border-color: rgba(29, 78, 216, 0.2);
}

.tone-ready {
  color: var(--success);
  background: rgba(15, 118, 110, 0.12);
  border-color: rgba(15, 118, 110, 0.2);
}

.tone-failed {
  color: var(--warning);
  background: rgba(180, 83, 9, 0.12);
  border-color: rgba(180, 83, 9, 0.2);
}

.processing-hero {
  margin-top: 24px;
  animation: fadeUp 0.8s ease;
}

.eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.3em;
  font-size: 12px;
  color: var(--muted);
  margin-bottom: 12px;
}

.processing-hero h1 {
  font-family: 'Fraunces', serif;
  font-size: clamp(32px, 4vw, 46px);
  margin: 0 0 16px;
}

.lead {
  font-size: 17px;
  color: var(--muted);
  margin: 0;
  max-width: 600px;
}

.processing-steps {
  margin-top: 28px;
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  animation: fadeUp 0.9s ease;
}

.step {
  display: grid;
  grid-template-columns: 40px 1fr;
  gap: 16px;
  padding: 16px 18px;
  border-radius: 20px;
  background: #ffffff;
  border: 1px solid rgba(226, 232, 240, 0.8);
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.06);
  align-items: start;
}

.step-index {
  width: 32px;
  height: 32px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 12px;
  color: var(--accent);
  background: rgba(29, 78, 216, 0.08);
  border: 1px solid rgba(29, 78, 216, 0.2);
}

.step-title {
  font-weight: 600;
  margin: 0 0 6px;
  color: var(--ink);
}

.step-copy {
  margin: 0;
  color: var(--muted);
  font-size: 14px;
}

.processing-footer {
  margin-top: 26px;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
}

.processing-meta {
  flex: 1 1 360px;
  display: flex;
  gap: 24px;
  justify-content: space-between;
  padding: 18px 20px;
  background: #f8fafc;
  border-radius: 20px;
  border: 1px solid rgba(226, 232, 240, 0.6);
}

.processing-alert {
  margin-top: 18px;
  padding: 16px 18px;
  border-radius: 18px;
  background: rgba(180, 83, 9, 0.08);
  border: 1px solid rgba(180, 83, 9, 0.22);
  color: #92400e;
  font-size: 14px;
}

.meta-label {
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.2em;
  color: var(--muted);
  margin: 0 0 8px;
}

.meta-value {
  font-size: 15px;
  margin: 0;
}

.processing-actions {
  flex: 0 0 auto;
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: flex-end;
}

.processing-actions button {
  border-radius: 999px;
  padding: 12px 18px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  border: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.processing-actions button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.processing-actions .primary {
  background: var(--accent);
  color: #fff;
  box-shadow: 0 12px 24px rgba(37, 99, 235, 0.25);
}

.processing-actions .secondary {
  background: #fff;
  border: 1px solid rgba(148, 163, 184, 0.4);
  color: var(--ink);
}

.processing-actions button:not(:disabled):hover {
  transform: translateY(-2px);
}

.processing-help {
  margin-top: 26px;
  font-size: 13px;
  color: var(--muted);
}

.processing-help a {
  color: var(--accent);
  text-decoration: none;
  font-weight: 600;
}

@keyframes rise {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes orbitFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(18px); }
}

@media (max-width: 900px) {
  .processing-steps {
    grid-template-columns: 1fr;
  }

  .processing-footer {
    flex-direction: column;
    align-items: stretch;
  }
}

@media (max-width: 720px) {
  .processing-card {
    padding: 32px 24px;
  }

  .processing-steps {
    grid-template-columns: 1fr;
  }

  .step {
    grid-template-columns: 1fr;
  }

  .step-index {
    margin-bottom: 8px;
  }

  .processing-actions {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
